import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Stack,
  Container,
  Tooltip,
  Divider
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import { statsApi, analyticsApi, transactionsApi, auctionsApi, usersApi, secondHandApi, bidsApi, productsApi, reviewsApi, bidPackConfigsApi, ordersApi } from '../../utils/apiClient';
import AdminSidebar, { ADMIN_TABS } from '../../components/Admin/AdminSidebar';
import { useTheme as useThemeContext } from '../../contexts/ThemeContext';
import AdminOverview from '../../components/Admin/AdminOverview';
import AdminAnalytics from '../../components/Admin/AdminAnalytics';
import AdminUsers from '../../components/Admin/AdminUsers';
import AdminRoles from '../../components/Admin/AdminRoles';
import AdminAuctions from '../../components/Admin/AdminAuctions';
import AdminSecondHand from '../../components/Admin/AdminSecondHand';
import AdminProducts from '../../components/Admin/AdminProducts';
import AdminBrands from '../../components/Admin/AdminBrands';
import AdminBids from '../../components/Admin/AdminBids';
import AdminReviews from '../../components/Admin/AdminReviews';
import AdminTransactions from '../../components/Admin/AdminTransactions';
import AdminOrders from '../../components/Admin/AdminOrders';
import AdminServerHealth from '../../components/Admin/AdminServerHealth';
import AdminGoogleAnalytics from '../../components/Admin/AdminGoogleAnalytics';
import AdminCoupons from '../../components/Admin/AdminCoupons';
import AdminPacks from '../../components/Admin/AdminPacks';
import AdminPaymentSettings from '../../components/Admin/AdminPaymentSettings';

const drawerWidth = 280;

function AdminDashboard() {
  const { hasRole } = usePermissions();
  const toast = useToast();
  const { isDarkMode, toggleTheme } = useThemeContext();
  const muiTheme = useMuiTheme();
  const isLgUp = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const activeTabMeta = ADMIN_TABS.find(tab => tab.key === activeTab);
  const activeTabLabel = activeTabMeta?.label ?? 'Admin Dashboard';

  const handleDrawerToggle = () => {
    setMobileNavOpen(prev => !prev);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    if (!isLgUp) {
      setMobileNavOpen(false);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AdminSidebar activeTab={activeTab} onTabChange={handleTabChange} />
      <Divider />
      <Box sx={{ p: 3 }}>
        <Typography variant="caption" color="text.secondary">
          All systems operational.
        </Typography>
      </Box>
    </Box>
  );

  // KPI state
  const [kpis, setKpis] = useState({
    users: '-',
    auctions: '-',
    pendingSecondHandBids: '-',
    loading: true,
    error: null,
  });

  // In AdminDashboard function, after kpis state:
  const [moreKpis, setMoreKpis] = useState({ products: '-', transactions: '-', revenue: '-', activeAuctions: '-' });
  const [moreKpisLoading, setMoreKpisLoading] = useState(false);
  const [moreKpisError, setMoreKpisError] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [activityLogError, setActivityLogError] = useState(null);
  const [chartData, setChartData] = useState({});

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ 
    username: '', 
    email: '', 
    role: 'Observer', 
    status: 'active', 
    firstName: '', 
    lastName: ''
  });
  const [userFormError, setUserFormError] = useState(null);
  const [userFormLoading, setUserFormLoading] = useState(false);

  // Auctions state
  const [auctions, setAuctions] = useState([]);
  const [auctionsLoading, setAuctionsLoading] = useState(false);
  const [auctionsError, setAuctionsError] = useState(null);
  const [auctionsPage, setAuctionsPage] = useState(1);
  const [auctionsTotalPages, setAuctionsTotalPages] = useState(1);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [auctionForm, setAuctionForm] = useState({ 
    productId: '', 
    minBid: '', 
    bidIncrement: '',
    // increment now derived from pack configuration (selected or global)
    increment: 3,
    packConfigId: '',
    startTime: '', 
    durationMinutes: '',
    mode: 'fixed', 
    autoExtendDuration: 10,
    autoExtendTriggerSeconds: 60,
    status: 'pending' 
  });
  const [auctionFormError, setAuctionFormError] = useState(null);
  const [auctionFormLoading, setAuctionFormLoading] = useState(false);

  // Available pack configurations for auction creation
  const [availablePackConfigs, setAvailablePackConfigs] = useState([]);

  // Second-hand auctions state
  const [secondHand, setSecondHand] = useState([]);
  const [secondHandLoading, setSecondHandLoading] = useState(false);
  const [secondHandError, setSecondHandError] = useState(null);
  const [secondHandPage, setSecondHandPage] = useState(1);
  const [secondHandTotalPages, setSecondHandTotalPages] = useState(1);
  const [showSecondHandModal, setShowSecondHandModal] = useState(false);
  const [editingSecondHand, setEditingSecondHand] = useState(null);
  const [secondHandForm, setSecondHandForm] = useState({ 
    name: '', 
    description: '', 
    category: '', 
    condition: '', 
    reservePrice: '', 
    duration: '',
    status: 'pending' 
  });
  const [secondHandFormError, setSecondHandFormError] = useState(null);
  const [secondHandFormLoading, setSecondHandFormLoading] = useState(false);
  const [showBidValidationModal, setShowBidValidationModal] = useState(false);
  const [selectedSecondHand, setSelectedSecondHand] = useState(null);

  // Products state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productsPage, setProductsPage] = useState(1);
  const [productsTotalPages, setProductsTotalPages] = useState(1);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ 
    name: '', 
    description: '', 
    category: '', 
    subCategory: '',
    price: '', 
    stock: '999999', // Default infinite stock
    soldOut: false, // New field for sold out toggle
    status: 'active',
    condition: 'new',
    tags: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    isReservable: false
  });
  const [productFormError, setProductFormError] = useState(null);
  const [productFormLoading, setProductFormLoading] = useState(false);
  const [productFormFiles, setProductFormFiles] = useState([]);

  // Bids state
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidsError, setBidsError] = useState(null);
  const [bidsPage, setBidsPage] = useState(1);
  const [bidsTotalPages, setBidsTotalPages] = useState(1);
  const [showBidModal, setShowBidModal] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [bidForm, setBidForm] = useState({ amount: '', status: 'active' });
  const [bidFormError, setBidFormError] = useState(null);
  const [bidFormLoading, setBidFormLoading] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsTotalPages, setReviewsTotalPages] = useState(1);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: '', comment: '' });
  const [reviewFormError, setReviewFormError] = useState(null);
  const [reviewFormLoading, setReviewFormLoading] = useState(false);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotalPages, setTransactionsTotalPages] = useState(1);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState({ eventsByType: [], dailyEvents: [], topUsers: [] });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  // Search state variables
  const [auctionsSearch, setAuctionsSearch] = useState('');
  const [secondHandSearch, setSecondHandSearch] = useState('');
  const [productsSearch, setProductsSearch] = useState('');

  // Fetch users
  const fetchUsers = async (page = 1, search = '') => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      const res = await usersApi.getAll(params);
      setUsers(res.data || []);
      setUsersTotalPages(res.pagination?.pages || 1);
      setUsersPage(res.pagination?.page || 1);
    } catch (err) {
      setUsersError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch auctions
  const fetchAuctions = async (page = 1) => {
    setAuctionsLoading(true);
    setAuctionsError(null);
    try {
      const params = { page, limit: 10 };
      const res = await auctionsApi.getAll(params);
      setAuctions(res.data || []);
      setAuctionsTotalPages(res.pagination?.pages || 1);
      setAuctionsPage(res.pagination?.page || 1);
    } catch (err) {
      setAuctionsError('Failed to load auctions');
    } finally {
      setAuctionsLoading(false);
    }
  };

  // Fetch second-hand auctions
  const fetchSecondHand = async (page = 1, search = '') => {
    setSecondHandLoading(true);
    setSecondHandError(null);
    try {
      const params = { page, limit: 10, status: 'all' };
      if (search) params.search = search;
      const res = await secondHandApi.getAll(params);
      setSecondHand(res.data || []);
      setSecondHandTotalPages(res.pagination?.pages || 1);
      setSecondHandPage(res.pagination?.page || 1);
    } catch (err) {
      console.error('Error fetching second-hand auctions:', err);
      setSecondHandError('Failed to load second-hand auctions');
    } finally {
      setSecondHandLoading(false);
    }
  };

  // Fetch products
  const fetchProducts = async (page = 1) => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const params = { page, limit: 10 };
      const res = await productsApi.getAll(params);
      // Handle the new API response structure
      if (res.success && res.data) {
        setProducts(res.data.products || []);
        setProductsTotalPages(res.data.pagination?.totalPages || 1);
        setProductsPage(res.data.pagination?.currentPage || 1);
      } else {
        // Fallback for old structure
        setProducts(res.data || []);
        setProductsTotalPages(res.pagination?.pages || 1);
        setProductsPage(res.pagination?.page || 1);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setProductsError('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  // Fetch bids
  const fetchBids = async (page = 1) => {
    setBidsLoading(true);
    setBidsError(null);
    try {
      const params = { page, limit: 10 };
      const res = await bidsApi.getAll(params);
      setBids(res.data || []);
      setBidsTotalPages(res.pagination?.pages || 1);
      setBidsPage(res.pagination?.page || 1);
    } catch (err) {
      setBidsError('Failed to load bids');
    } finally {
      setBidsLoading(false);
    }
  };

  // Fetch reviews
  const fetchReviews = async (page = 1) => {
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const params = { page, limit: 10 };
      const res = await reviewsApi.getAll(params);
      setReviews(res.data || []);
      setReviewsTotalPages(res.pagination?.pages || 1);
      setReviewsPage(res.pagination?.page || 1);
    } catch (err) {
      setReviewsError('Failed to load reviews');
    } finally {
      setReviewsLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (page = 1) => {
    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
  const params = { page, limit: 10 };
  const res = await transactionsApi.list(params);
  // Expecting { success, data: [...], pagination }
  setTransactions(res.data || []);
  setTransactionsTotalPages(res.pagination?.pages || 1);
  setTransactionsPage(res.pagination?.page || page);
    } catch (err) {
      setTransactionsError('Failed to load transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Fetch orders
  const fetchOrders = async (page = 1, params = {}) => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const qp = { page, limit: 10, ...params };
      const res = await ordersApi.list(qp);
      setOrders(res.data || []);
      setOrdersTotalPages(res.pagination?.pages || res.data?.pagination?.totalPages || 1);
      setOrdersPage(res.pagination?.page || page);
    } catch (err) {
      setOrdersError('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  // Fetch analytics
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const res = await analyticsApi.getDashboard();
      setAnalytics({
        eventsByType: res.data?.eventsByType || [],
        dailyEvents: res.data?.dailyEvents || [],
        topUsers: res.data?.topUsers || []
      });
    } catch (err) {
      setAnalyticsError('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch available pack configurations for auction creation
  const fetchAvailablePackConfigs = async () => {
    try {
      const res = await bidPackConfigsApi.getAvailable();
      const configs = res.data || [];
      setAvailablePackConfigs(configs);
      // Derive increment immediately
      setAuctionForm(f => {
        let selected = f.packConfigId ? configs.find(c => c._id === f.packConfigId) : null;
        if (!selected) selected = configs.find(c => c.scope === 'global');
        if (selected && (selected.increment || selected.defaultIncrement)) {
          return { ...f, increment: selected.increment || selected.defaultIncrement };
        }
        return f;
      });
    } catch (err) {
      console.error('Failed to fetch pack configurations:', err);
    }
  };

  // Fetch more KPIs and activity log
  const fetchMoreKpisAndActivity = async () => {
    setMoreKpisLoading(true);
    setMoreKpisError(null);
    setActivityLogLoading(true);
    setActivityLogError(null);
    try {
      // Products and transactions from platform stats
      const statsRes = await statsApi.getPlatformStats();
      setMoreKpis({
        products: statsRes?.stats?.products ?? '-',
        transactions: statsRes?.stats?.transactions ?? '-',
        revenue: '-', // Placeholder, can be fetched from transactionsApi if available
        activeAuctions: statsRes?.stats?.auctionRooms ?? '-',
        reviews: statsRes?.stats?.reviews ?? '-',
      });
      // Set KPIs
      setKpis(k => ({
        ...k,
        users: statsRes?.stats?.users ?? '-',
        auctions: statsRes?.stats?.auctions ?? '-',
        onlineUsers: statsRes?.stats?.onlineUsers ?? '-',
        currentParticipants: statsRes?.stats?.currentParticipants ?? '-',
        currentObservers: statsRes?.stats?.currentObservers ?? '-',
        pendingSecondHandAuctions: statsRes?.stats?.pendingSecondHandAuctions ?? '-',
        pendingProductApprovals: statsRes?.stats?.pendingProductApprovals ?? '-',
        pendingSecondHandBids: statsRes?.stats?.pendingSecondHandBids ?? '-',
      }));
      // Role distribution
      setChartData(cd => ({
        ...cd,
        roleDistribution: statsRes?.stats?.roleDistribution || []
      }));
      // Recent activity log (last 10 events)
      const activityRes = await analyticsApi.getAll({ page: 1, limit: 10 });
      setActivityLog(activityRes.data || []);
    } catch (err) {
      setMoreKpisError('Failed to load additional KPIs');
      setActivityLogError('Failed to load activity log');
    } finally {
      setMoreKpisLoading(false);
      setActivityLogLoading(false);
    }
  };

  // Fetch chart data for overview (placeholder derived series)
  const fetchOverviewCharts = async () => {
    try {
      const today = new Date();
      const makeDate = (d) => d.toISOString().slice(0,10);
      const dates = [...Array(7)].map((_,i) => { const d = new Date(today); d.setDate(d.getDate() - (6 - i)); return d; });
      setChartData(cd => ({
        ...cd,
        userActivity: dates.map(d => ({ date: makeDate(d), count: Math.floor(Math.random()*10)+1 })),
        auctionsCreated: dates.map(d => ({ date: makeDate(d), count: Math.floor(Math.random()*4) })),
        productsAdded: dates.map(d => ({ date: makeDate(d), count: Math.floor(Math.random()*6) })),
      }));
    } catch {}
  };

  useEffect(() => {
    const fetchKPIs = async () => {
      setKpis(k => ({ ...k, loading: true, error: null }));
      try {
        const statsRes = await statsApi.getPlatformStats();
        const s = statsRes?.stats || {};
        setKpis({
          users: s.users ?? '-',
          auctions: s.auctions ?? s.totalAuctions ?? '-',
          pendingSecondHandBids: s.pendingSecondHandAuctions ?? '-',
          onlineUsers: s.onlineUsers ?? '-',
          currentParticipants: s.currentParticipants ?? '-',
          currentObservers: s.currentObservers ?? '-',
          pendingSecondHandAuctions: s.pendingSecondHandAuctions ?? '-',
          pendingProductApprovals: s.pendingProductApprovals ?? '-',
          loading: false,
          error: null,
        });
        setMoreKpis(m => ({
          ...m,
          products: s.products ?? '-',
          transactions: s.transactions ?? '-',
          reviews: s.reviews ?? '-',
          activeAuctions: s.auctions ?? '-',
        }));
        setChartData(cd => ({
          ...cd,
          liveRoles: [
            { role: 'Participants', count: s.currentParticipants || 0 },
            { role: 'Observers', count: s.currentObservers || 0 }
          ],
          roleDistribution: s.roleDistribution || cd.roleDistribution
        }));
      } catch (err) {
        setKpis(k => ({ ...k, loading: false, error: 'Failed to load KPIs' }));
      }
    };
    if (activeTab === 'overview') fetchKPIs();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchMoreKpisAndActivity();
      fetchOverviewCharts();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers(usersPage, usersSearch);
    if (activeTab === 'auctions') {
      fetchAuctions(auctionsPage);
      // Also fetch products for auction form
      if (products.length === 0) {
        fetchProducts(1);
      }
      // Fetch available pack configurations
      fetchAvailablePackConfigs();
    }
    if (activeTab === 'secondHand') fetchSecondHand(secondHandPage, secondHandSearch);
    if (activeTab === 'products') fetchProducts(productsPage);
    if (activeTab === 'bids') fetchBids(bidsPage);
    if (activeTab === 'reviews') fetchReviews(reviewsPage);
    if (activeTab === 'transactions') fetchTransactions(transactionsPage);
    if (activeTab === 'orders') fetchOrders(ordersPage);
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'serverHealth') {
      // No specific fetch logic for server health, just render the component
    }
    if (activeTab === 'googleAnalytics') {
      // No specific fetch logic for Google Analytics, just render the component
    }
    // eslint-disable-next-line
  }, [activeTab, usersPage, usersSearch, auctionsPage, secondHandPage, productsPage, bidsPage, reviewsPage, transactionsPage]);

  // User modal handlers
  const openUserModal = (user = null) => {
    setEditingUser(user);
    setUserForm(user ? {
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'Observer',
      status: user.status || 'active',
      firstName: user.profile?.firstName || '',
      lastName: user.profile?.lastName || '',
      permissions: user.permissions || {
        'users.view': false,
        'users.create': false,
        'users.edit': false,
        'users.delete': false,
        'users.roles': false,
        'auctions.view': false,
        'auctions.create': false,
        'auctions.edit': false,
        'auctions.delete': false,
        'auctions.approve': false,
        'products.view': false,
        'products.create': false,
        'products.edit': false,
        'products.delete': false,
        'products.approve': false,
        'transactions.view': false,
        'transactions.manage': false,
        'reports.view': false,
        'refunds.process': false,
        'system.settings': false,
        'system.logs': false,
        'system.backup': false,
        'system.security': false
      }
    } : { 
      username: '', 
      email: '', 
      role: 'Observer', 
      status: 'active', 
      firstName: '', 
      lastName: '',
      permissions: {
        'users.view': false,
        'users.create': false,
        'users.edit': false,
        'users.delete': false,
        'users.roles': false,
        'auctions.view': false,
        'auctions.create': false,
        'auctions.edit': false,
        'auctions.delete': false,
        'auctions.approve': false,
        'products.view': false,
        'products.create': false,
        'products.edit': false,
        'products.delete': false,
        'products.approve': false,
        'transactions.view': false,
        'transactions.manage': false,
        'reports.view': false,
        'refunds.process': false,
        'system.settings': false,
        'system.logs': false,
        'system.backup': false,
        'system.security': false
      }
    });
    setUserFormError(null);
    setShowUserModal(true);
  };
  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserFormError(null);
  };
  const handleUserFormChange = e => {
    const { name, value } = e.target;
    setUserForm(f => ({ ...f, [name]: value }));
  };
  const handleUserFormSubmit = async e => {
    e.preventDefault();
    setUserFormLoading(true);
    setUserFormError(null);
    try {
      if (editingUser) {
        await usersApi.update(editingUser._id, {
          username: userForm.username,
          email: userForm.email,
          role: userForm.role,
          status: userForm.status,
          profile: { firstName: userForm.firstName, lastName: userForm.lastName }
        });
      } else {
        await usersApi.create({
          username: userForm.username,
          email: userForm.email,
          role: userForm.role,
          status: userForm.status,
          profile: { firstName: userForm.firstName, lastName: userForm.lastName },
          password: 'changeme123' // Default password, should be changed by user
        });
      }
      closeUserModal();
      fetchUsers(usersPage, usersSearch);
    } catch (err) {
      setUserFormError('Failed to save user');
    } finally {
      setUserFormLoading(false);
    }
  };
  const handleDeleteUser = async user => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(user._id);
      fetchUsers(usersPage, usersSearch);
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  // Auction modal handlers
  const openAuctionModal = (auction = null) => {
    setEditingAuction(auction);
    const start = auction?.startTime ? new Date(auction.startTime) : null;
    const end = auction?.endTime ? new Date(auction.endTime) : null;
    const durationMinutes = (start && end) ? Math.round((end - start) / 60000) : '';
    setAuctionForm(auction ? {
      productId: auction.productId || '',
      minBid: auction.minBid || '',
      bidIncrement: auction.bidIncrement || '',
      // retain stored increment for display; will re-derive after configs load
      increment: auction.increment || 3,
      packConfigId: auction.packConfigId || '',
      startTime: start ? start.toISOString().slice(0, 16) : '',
      durationMinutes,
      mode: auction.mode || 'fixed',
      autoExtendDuration: auction.autoExtend?.duration || 10,
      autoExtendTriggerSeconds: auction.autoExtend?.triggerSeconds || 60,
      status: auction.status || 'pending'
  } : { 
      productId: '', 
      minBid: '', 
      bidIncrement: '', 
      increment: 3,
      packConfigId: '',
      startTime: '', 
      durationMinutes: '',
      mode: 'fixed', 
      autoExtendDuration: 10,
      autoExtendTriggerSeconds: 60,
      status: 'pending' 
    });
    setAuctionFormError(null);
    setShowAuctionModal(true);
    // Fetch products if not already loaded
  if (products.length === 0) fetchProducts(1);
  if (availablePackConfigs.length === 0) fetchAvailablePackConfigs();
  };
  const closeAuctionModal = () => {
    setShowAuctionModal(false);
    setEditingAuction(null);
    setAuctionFormError(null);
  };
  const handleAuctionFormChange = e => {
    const { name, value } = e.target;
    setAuctionForm(f => {
      const next = { ...f, [name]: value };
      if (name === 'packConfigId') {
        const cfg = availablePackConfigs.find(c => c._id === value) || availablePackConfigs.find(c => c.scope === 'global');
        if (cfg && (cfg.increment || cfg.defaultIncrement)) {
          next.increment = cfg.increment || cfg.defaultIncrement;
        }
      }
      return next;
    });
  };
  const handleAuctionFormSubmit = async e => {
    e.preventDefault();
    setAuctionFormLoading(true);
    setAuctionFormError(null);
    
    // Compute end time from duration
    if (!auctionForm.startTime || !auctionForm.durationMinutes) {
      setAuctionFormError('Start time and duration are required');
      setAuctionFormLoading(false);
      return;
    }
    const startDate = new Date(auctionForm.startTime);
    const durationMs = parseInt(auctionForm.durationMinutes, 10) * 60000;
    if (isNaN(durationMs) || durationMs <= 0) {
      setAuctionFormError('Duration must be a positive number of minutes');
      setAuctionFormLoading(false);
      return;
    }
    const computedEnd = new Date(startDate.getTime() + durationMs);
    
    // Build payload without frontend-only fields
    const payload = {
      productId: auctionForm.productId,
      minBid: parseFloat(auctionForm.minBid),
      bidIncrement: parseFloat(auctionForm.bidIncrement),
      // increment now enforced server-side via pack config; omit to avoid mismatch
      startTime: startDate.toISOString(),
      endTime: computedEnd.toISOString(),
      mode: auctionForm.mode,
      status: auctionForm.status
    };

    // Add pack configuration if selected
    if (auctionForm.packConfigId) {
      payload.packConfigId = auctionForm.packConfigId;
    }

    if (auctionForm.mode === 'auto-extend') {
      payload.autoExtend = {
        duration: parseInt(auctionForm.autoExtendDuration, 10) || 10,
        triggerSeconds: parseInt(auctionForm.autoExtendTriggerSeconds, 10) || 60
      };
    }
    
    try {
      if (editingAuction) {
        await auctionsApi.update(editingAuction._id, payload);
      } else {
        await auctionsApi.create(payload);
      }
      closeAuctionModal();
      fetchAuctions(auctionsPage);
    } catch (err) {
      setAuctionFormError('Failed to save auction');
    } finally {
      setAuctionFormLoading(false);
    }
  };
  const handleDeleteAuction = async auction => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to delete this auction?')) return;
    
    // Check if auction is active
    if (auction.status === 'active') {
      toast.warning('Cannot delete active auctions. Please change status to "closed" or "canceled" first.');
      return;
    }
    
    try {
      // Get current user info
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
      }
      
      const response = await auctionsApi.delete(auction._id);
      
      if (response && response.success !== false) {
        fetchAuctions(auctionsPage);
      } else {
        toast.error('Failed to delete auction: ' + (response?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Delete auction error:', err);
      toast.error('Failed to delete auction: ' + (err.message || 'Unknown error'));
    }
  };

  // Second-hand modal handlers
  const openSecondHandModal = (item = null) => {
    setEditingSecondHand(item);
    setSecondHandForm(item ? {
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      condition: item.condition || '',
      reservePrice: item.reservePrice || item.price || '',
      duration: item.duration || '',
      status: item.status || 'pending'
    } : { 
      name: '', 
      description: '', 
      category: '', 
      condition: '', 
      reservePrice: '', 
      duration: '',
      status: 'pending' 
    });
    setSecondHandFormError(null);
    setShowSecondHandModal(true);
  };
  const closeSecondHandModal = () => {
    setShowSecondHandModal(false);
    setEditingSecondHand(null);
    setSecondHandFormError(null);
  };
  const handleSecondHandFormChange = e => {
    const { name, value } = e.target;
    setSecondHandForm(f => ({ ...f, [name]: value }));
  };
  const handleSecondHandFormSubmit = async e => {
    e.preventDefault();
    setSecondHandFormLoading(true);
    setSecondHandFormError(null);
    try {
      if (editingSecondHand) {
        await secondHandApi.update(editingSecondHand._id, secondHandForm);
      } else {
        await secondHandApi.create(secondHandForm);
      }
      closeSecondHandModal();
      fetchSecondHand(secondHandPage, secondHandSearch);
    } catch (err) {
      setSecondHandFormError('Failed to save second-hand auction');
    } finally {
      setSecondHandFormLoading(false);
    }
  };
  const handleDeleteSecondHand = async item => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to delete this second-hand auction?')) return;
    try {
      await secondHandApi.delete(item._id);
      fetchSecondHand(secondHandPage, secondHandSearch);
    } catch (err) {
      toast.error('Failed to delete second-hand auction');
    }
  };

  // Bid validation modal handlers
  const openBidValidationModal = item => {
    setSelectedSecondHand(item);
    setShowBidValidationModal(true);
  };
  const closeBidValidationModal = () => {
    setShowBidValidationModal(false);
    setSelectedSecondHand(null);
  };


  // Product modal handlers
  const openProductModal = (product = null) => {
    setEditingProduct(product);
    setProductForm(product ? {
      name: product.name || '',
      description: product.description || '',
      category: product.category || '',
      subCategory: product.subCategory || '',
      // `brand` comes back populated on some endpoints and as a bare id on others
      brand: product.brand?._id || product.brand || '',
      price: product.price || '',
      stock: product.stock || '999999',
      soldOut: Boolean(product.soldOut),
      status: product.status || 'active',
      condition: product.condition || 'new',
      tags: Array.isArray(product.tags) ? product.tags.join(', ') : product.tags || '',
      weight: product.dimensions?.weight || '',
      length: product.dimensions?.length || '',
      width: product.dimensions?.width || '',
      height: product.dimensions?.height || '',
      isReservable: !!product.isReservable
    } : { 
      name: '',
      description: '',
      category: '',
      subCategory: '',
      brand: '',
      price: '',
      stock: '999999',
      soldOut: false,
      status: 'active',
      condition: 'new',
      tags: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      isReservable: false
    });
    setProductFormError(null);
    setShowProductModal(true);
  };
  const closeProductModal = () => {
    setShowProductModal(false);
    setEditingProduct(null);
    setProductFormError(null);
  };
  const handleProductFormChange = e => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setProductForm(f => ({ ...f, [name]: fieldValue }));
  };
  const handleProductFormSubmit = async e => {
    e.preventDefault();
    setProductFormLoading(true);
    setProductFormError(null);
    try {
      let formData;
      if (productFormFiles && productFormFiles.length > 0) {
        formData = new FormData();
        formData.append('name', productForm.name);
        formData.append('description', productForm.description);
        formData.append('category', productForm.category);
        formData.append('subCategory', productForm.subCategory);
        if (productForm.brand) {
          formData.append('brand', productForm.brand);
        }
        formData.append('price', productForm.price);
        formData.append('stock', productForm.stock);
        formData.append('status', productForm.status);
        formData.append('condition', productForm.condition);
        formData.append('isReservable', productForm.isReservable ? 'true' : 'false');
        formData.append('soldOut', productForm.soldOut ? 'true' : 'false');
        formData.append('tags', JSON.stringify(productForm.tags ? productForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []));
        formData.append('dimensions', JSON.stringify({
          weight: productForm.weight ? parseFloat(productForm.weight) : undefined,
          length: productForm.length ? parseFloat(productForm.length) : undefined,
          width: productForm.width ? parseFloat(productForm.width) : undefined,
          height: productForm.height ? parseFloat(productForm.height) : undefined
        }));
        for (let i = 0; i < productFormFiles.length; i++) {
          formData.append('images', productFormFiles[i]);
        }
      }
      if (editingProduct) {
        if (formData) {
          await productsApi.update(editingProduct._id, formData, true);
        } else {
          await productsApi.update(editingProduct._id, { ...productForm, isReservable: !!productForm.isReservable, soldOut: !!productForm.soldOut });
        }
      } else {
        if (formData) {
          await productsApi.create(formData, true);
        } else {
          await productsApi.create({ ...productForm, isReservable: !!productForm.isReservable, soldOut: !!productForm.soldOut });
        }
      }
      closeProductModal();
      setProductFormFiles([]);
      fetchProducts(productsPage);
    } catch (err) {
      console.error('Product form submission error:', err);
      setProductFormError(
        err.response?.data?.message || 
        err.response?.data?.error || 
        err.message || 
        'Failed to save product'
      );
    } finally {
      setProductFormLoading(false);
    }
  };
  const handleDeleteProduct = async product => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsApi.delete(product._id);
      fetchProducts(productsPage);
    } catch (err) {
      toast.error('Failed to delete product: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const handleBulkProductStatusUpdate = async (productIds, newStatus) => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm(`Are you sure you want to update ${productIds.length} products to ${newStatus}?`)) return;
    
    try {
      await productsApi.bulkUpdateStatus(productIds, newStatus);
      fetchProducts(productsPage);
    } catch (err) {
      toast.error('Failed to update products: ' + (err.response?.data?.message || err.message));
    }
  };
  const handleApproveProduct = async product => {
    try {
      await productsApi.approve(product._id);
      fetchProducts(productsPage);
    } catch (err) {
      toast.error('Failed to approve product');
    }
  };
  const handleRejectProduct = async product => {
    try {
      await productsApi.reject(product._id);
      fetchProducts(productsPage);
    } catch (err) {
      toast.error('Failed to reject product');
    }
  };

  // Bid modal handlers
  const openBidModal = (bid = null) => {
    setEditingBid(bid);
    setBidForm(bid ? {
      amount: bid.amount || '',
      status: bid.status || 'active'
    } : { amount: '', status: 'active' });
    setBidFormError(null);
    setShowBidModal(true);
  };
  const closeBidModal = () => {
    setShowBidModal(false);
    setEditingBid(null);
    setBidFormError(null);
  };
  const handleBidFormChange = e => {
    const { name, value } = e.target;
    setBidForm(f => ({ ...f, [name]: value }));
  };
  const handleBidFormSubmit = async e => {
    e.preventDefault();
    setBidFormLoading(true);
    setBidFormError(null);
    try {
      if (editingBid) {
        await bidsApi.update(editingBid._id, bidForm);
      } else {
        await bidsApi.create(bidForm);
      }
      closeBidModal();
      fetchBids(bidsPage);
    } catch (err) {
      setBidFormError('Failed to save bid');
    } finally {
      setBidFormLoading(false);
    }
  };
  const handleDeleteBid = async bid => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to delete this bid?')) return;
    try {
      await bidsApi.delete(bid._id);
      fetchBids(bidsPage);
    } catch (err) {
      toast.error('Failed to delete bid');
    }
  };

  // Review modal handlers
  const openReviewModal = (review = null) => {
    setEditingReview(review);
    setReviewForm(review ? {
      rating: review.rating || '',
      comment: review.comment || ''
    } : { rating: '', comment: '' });
    setReviewFormError(null);
    setShowReviewModal(true);
  };
  const closeReviewModal = () => {
    setShowReviewModal(false);
    setEditingReview(null);
    setReviewFormError(null);
  };
  const handleReviewFormChange = e => {
    const { name, value } = e.target;
    setReviewForm(f => ({ ...f, [name]: value }));
  };
  const handleReviewFormSubmit = async e => {
    e.preventDefault();
    setReviewFormLoading(true);
    setReviewFormError(null);
    try {
      if (editingReview) {
        await reviewsApi.update(editingReview._id, reviewForm);
      } else {
        await reviewsApi.create(reviewForm);
      }
      closeReviewModal();
      fetchReviews(reviewsPage);
    } catch (err) {
      setReviewFormError('Failed to save review');
    } finally {
      setReviewFormLoading(false);
    }
  };
  const handleDeleteReview = async review => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await reviewsApi.delete(review._id);
      fetchReviews(reviewsPage);
    } catch (err) {
      toast.error('Failed to delete review');
    }
  };

  // Transaction modal handlers
  const openTransactionModal = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };
  const closeTransactionModal = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  // Bid approval handler
  const handleApproveBid = async (item) => {
    try {
      await secondHandApi.update(item._id, { status: 'approved' });
      fetchSecondHand(secondHandPage, secondHandSearch);
    } catch (err) {
      toast.error('Failed to approve second-hand auction');
    }
  };

  // Second-hand search handler
  const handleSecondHandSearch = (search) => {
    setSecondHandSearch(search);
    fetchSecondHand(1, search);
  };

  // Only allow admins
  if (!hasRole('Admin')) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        backgroundImage: isDarkMode
          ? 'radial-gradient(circle at 82% -10%, rgba(118,87,255,.17), transparent 34%), radial-gradient(circle at 40% 110%, rgba(69,92,255,.08), transparent 38%)'
          : 'radial-gradient(circle at 82% -10%, rgba(118,87,255,.11), transparent 34%), radial-gradient(circle at 40% 110%, rgba(69,92,255,.06), transparent 38%)',
      }}
    >
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          backdropFilter: 'blur(24px) saturate(145%)',
          backgroundImage: 'none',
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
          bgcolor: muiTheme.vitria?.nav,
          boxShadow: isDarkMode ? '0 12px 42px rgba(0,0,0,.15)' : '0 12px 42px rgba(34,25,75,.05)',
        }}
      >
        <Toolbar sx={{ minHeight: 72, px: { xs: 2, md: 3, lg: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { lg: 'none' } }}
            >
              <MenuRoundedIcon />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0, fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
                {activeTabLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Manage and monitor platform operations
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
              <IconButton
                color="inherit"
                onClick={toggleTheme}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${muiTheme.palette.divider}`,
                  bgcolor: muiTheme.vitria?.input,
                }}
              >
                {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }} aria-label="admin navigation">
        <Drawer
          variant={isLgUp ? 'permanent' : 'temporary'}
          open={isLgUp ? true : mobileNavOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              borderRight: `1px solid ${muiTheme.palette.divider}`,
              boxSizing: 'border-box',
              bgcolor: isDarkMode ? 'rgba(14,13,24,.96)' : 'rgba(255,255,255,.96)',
              backdropFilter: 'blur(24px)',
              boxShadow: isDarkMode ? '16px 0 45px rgba(0,0,0,.14)' : '16px 0 45px rgba(34,25,75,.05)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 72, lg: 88 } }} />
        <Container maxWidth="xl" sx={{ pb: 6, pt: { xs: 3, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
          {activeTab === 'overview' && (
            <AdminOverview
              kpis={kpis}
              moreKpis={moreKpis}
              moreKpisLoading={moreKpisLoading}
              moreKpisError={moreKpisError}
              activityLog={activityLog}
              activityLogLoading={activityLogLoading}
              activityLogError={activityLogError}
              chartData={chartData}
            />
          )}

          {activeTab === 'users' && (
            <AdminUsers
              users={users}
              usersLoading={usersLoading}
              usersError={usersError}
              usersPage={usersPage}
              usersTotalPages={usersTotalPages}
              usersSearch={usersSearch}
              setUsersSearch={setUsersSearch}
              showUserModal={showUserModal}
              editingUser={editingUser}
              userForm={userForm}
              userFormError={userFormError}
              userFormLoading={userFormLoading}
              openUserModal={openUserModal}
              closeUserModal={closeUserModal}
              handleUserFormSubmit={handleUserFormSubmit}
              handleUserFormChange={handleUserFormChange}
              handleDeleteUser={handleDeleteUser}
              fetchUsers={fetchUsers}
            />
          )}

          {activeTab === 'roles' && (
            <AdminRoles />
          )}

          {activeTab === 'auctions' && (
            <AdminAuctions
              auctions={auctions}
              auctionsLoading={auctionsLoading}
              auctionsError={auctionsError}
              auctionsPage={auctionsPage}
              auctionsTotalPages={auctionsTotalPages}
              auctionsSearch={auctionsSearch}
              setAuctionsSearch={setAuctionsSearch}
              products={products}
              showAuctionModal={showAuctionModal}
              editingAuction={editingAuction}
              auctionForm={auctionForm}
              auctionFormError={auctionFormError}
              auctionFormLoading={auctionFormLoading}
              openAuctionModal={openAuctionModal}
              closeAuctionModal={closeAuctionModal}
              handleAuctionFormChange={handleAuctionFormChange}
              handleAuctionFormSubmit={handleAuctionFormSubmit}
              handleDeleteAuction={handleDeleteAuction}
              fetchAuctions={fetchAuctions}
              availablePackConfigs={availablePackConfigs}
            />
          )}

          {activeTab === 'secondHand' && (
            <AdminSecondHand
              secondHand={secondHand}
              secondHandLoading={secondHandLoading}
              secondHandError={secondHandError}
              secondHandPage={secondHandPage}
              secondHandTotalPages={secondHandTotalPages}
              secondHandSearch={secondHandSearch}
              setSecondHandSearch={handleSecondHandSearch}
              showSecondHandModal={showSecondHandModal}
              editingSecondHand={editingSecondHand}
              secondHandForm={secondHandForm}
              secondHandFormError={secondHandFormError}
              secondHandFormLoading={secondHandFormLoading}
              showBidValidationModal={showBidValidationModal}
              selectedSecondHand={selectedSecondHand}
              openSecondHandModal={openSecondHandModal}
              closeSecondHandModal={closeSecondHandModal}
              handleSecondHandFormChange={handleSecondHandFormChange}
              handleSecondHandFormSubmit={handleSecondHandFormSubmit}
              handleDeleteSecondHand={handleDeleteSecondHand}
              openBidValidationModal={openBidValidationModal}
              closeBidValidationModal={closeBidValidationModal}
              handleApproveBid={handleApproveBid}
              fetchSecondHand={fetchSecondHand}
            />
          )}

          {activeTab === 'products' && (
            <AdminProducts
              products={products}
              productsLoading={productsLoading}
              productsError={productsError}
              productsPage={productsPage}
              productsTotalPages={productsTotalPages}
              productsSearch={productsSearch}
              setProductsSearch={setProductsSearch}
              showProductModal={showProductModal}
              editingProduct={editingProduct}
              productForm={productForm}
              productFormError={productFormError}
              productFormLoading={productFormLoading}
              openProductModal={openProductModal}
              closeProductModal={closeProductModal}
              handleProductFormSubmit={handleProductFormSubmit}
              handleProductFormChange={handleProductFormChange}
              handleDeleteProduct={handleDeleteProduct}
              handleApproveProduct={handleApproveProduct}
              handleRejectProduct={handleRejectProduct}
              handleBulkProductStatusUpdate={handleBulkProductStatusUpdate}
              fetchProducts={fetchProducts}
              productFormFiles={productFormFiles}
              setProductFormFiles={setProductFormFiles}
            />
          )}

          {activeTab === 'brands' && <AdminBrands />}

          {activeTab === 'bids' && (
            <AdminBids
              bids={bids}
              bidsLoading={bidsLoading}
              bidsError={bidsError}
              bidsPage={bidsPage}
              bidsTotalPages={bidsTotalPages}
              showBidModal={showBidModal}
              editingBid={editingBid}
              bidForm={bidForm}
              bidFormError={bidFormError}
              bidFormLoading={bidFormLoading}
              openBidModal={openBidModal}
              closeBidModal={closeBidModal}
              handleBidFormSubmit={handleBidFormSubmit}
              handleBidFormChange={handleBidFormChange}
              handleDeleteBid={handleDeleteBid}
              fetchBids={fetchBids}
            />
          )}

          {activeTab === 'reviews' && (
            <AdminReviews
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              reviewsError={reviewsError}
              reviewsPage={reviewsPage}
              reviewsTotalPages={reviewsTotalPages}
              showReviewModal={showReviewModal}
              editingReview={editingReview}
              reviewForm={reviewForm}
              reviewFormError={reviewFormError}
              reviewFormLoading={reviewFormLoading}
              openReviewModal={openReviewModal}
              closeReviewModal={closeReviewModal}
              handleReviewFormSubmit={handleReviewFormSubmit}
              handleReviewFormChange={handleReviewFormChange}
              handleDeleteReview={handleDeleteReview}
              fetchReviews={fetchReviews}
            />
          )}

          {activeTab === 'transactions' && (
            <AdminTransactions
              transactions={transactions}
              transactionsLoading={transactionsLoading}
              transactionsError={transactionsError}
              transactionsPage={transactionsPage}
              transactionsTotalPages={transactionsTotalPages}
              showTransactionModal={showTransactionModal}
              selectedTransaction={selectedTransaction}
              openTransactionModal={openTransactionModal}
              closeTransactionModal={closeTransactionModal}
              fetchTransactions={fetchTransactions}
            />
          )}

          {activeTab === 'orders' && (
            <AdminOrders
              orders={orders}
              ordersLoading={ordersLoading}
              ordersError={ordersError}
              ordersPage={ordersPage}
              ordersTotalPages={ordersTotalPages}
              showOrderModal={showOrderModal}
              selectedOrder={selectedOrder}
              openOrderModal={(o) => { setSelectedOrder(o); setShowOrderModal(true); }}
              closeOrderModal={() => { setSelectedOrder(null); setShowOrderModal(false); }}
              fetchOrders={fetchOrders}
            />
          )}

          {activeTab === 'packsCoupons' && (
            <Stack spacing={4}>
              <AdminPacks />
              <AdminCoupons />
            </Stack>
          )}

          {activeTab === 'analytics' && (
            <AdminAnalytics
              analytics={analytics}
              analyticsLoading={analyticsLoading}
              analyticsError={analyticsError}
            />
          )}

          {activeTab === 'serverHealth' && (
            <AdminServerHealth />
          )}

          {activeTab === 'googleAnalytics' && (
            <AdminGoogleAnalytics />
          )}

          {activeTab === 'paymentSettings' && (
            <AdminPaymentSettings />
          )}
        </Container>
      </Box>
    </Box>
  );
}

export default AdminDashboard;
